<template>
  <div class="dashboard-wrapper">
    <!-- Toasts -->
    <CToaster :autohide="3000">
      <template v-for="info in infoList">
        <CToast
          :key="info.date"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </template>
    </CToaster>

    <!-- Header -->
    <header class="dash-header">
      <div>
        <h1 class="dash-title">Dashboard</h1>
        <p class="dash-subtitle">
          Inventory &amp; sales activity overview · {{ asOfLabel }}
        </p>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="loading" class="dash-loading">
      <CSpinner color="primary" />
      <p>Loading dashboard…</p>
    </div>

    <!-- Error -->
    <div v-else-if="errorMessage" class="dash-error">
      <i class="fas fa-exclamation-triangle"></i>
      <div>
        <strong>Could not load dashboard.</strong>
        <p>{{ errorMessage }}</p>
        <button class="dash-retry-btn" @click="loadDashboardData">Try again</button>
      </div>
    </div>

    <!-- Main content -->
    <template v-else>
      <!-- KPI strip (4 cards) -->
      <section class="dash-section">
        <div class="kpi-grid">
          <WidgetsDashboardKpi
            label="Registered this month"
            icon="cil-calendar"
            variant="primary"
            :value="kpi.registeredThisMonth"
            :delta="kpi.registeredThisMonthDelta"
            :delta-suffix="`vs ${previousMonthName}`"
            :sublabel="currentMonthName + ' ' + currentYear"
          />
          <WidgetsDashboardKpi
            label="Registered year to date"
            icon="cil-speedometer"
            variant="info"
            :value="kpi.registeredYtd"
            :delta="kpi.registeredYtdDelta"
            :delta-suffix="`vs ${lastYearNumber}`"
            :sublabel="currentYear.toString()"
          />
          <WidgetsDashboardKpi
            label="Available stock"
            icon="cil-inbox"
            variant="warning"
            :value="kpi.availableStock"
            sublabel="Currently open"
          />
          <WidgetsDashboardKpi
            label="Sold this year"
            icon="cil-check-circle"
            variant="success"
            :value="kpi.soldYtd"
            :delta="kpi.soldYtdDelta"
            :delta-suffix="`vs ${lastYearNumber}`"
            sublabel="Closed sales · count"
          />
        </div>
      </section>

      <!-- Row 1: Monthly trend + Top brands -->
      <section class="dash-section">
        <div class="charts-grid">
          <div class="chart-card">
            <div class="chart-header">
              <div>
                <h2 class="chart-title">Units Registered — Monthly Trend</h2>
                <p class="chart-subtitle">{{ currentYear }} (solid) vs {{ lastYearNumber }} (dotted)</p>
              </div>
            </div>
            <div class="chart-body">
              <CChartLine
                :datasets="trendLineDatasets"
                :labels="trendLineLabels"
                :options="trendLineOptions"
                style="height: 280px;"
              />
            </div>
          </div>

          <div class="chart-card">
            <div class="chart-header">
              <div>
                <h2 class="chart-title">Top Selling Brands</h2>
                <p class="chart-subtitle">Top 10 by units sold · this year vs last year</p>
              </div>
            </div>
            <div class="chart-body">
              <CChartBar
                :datasets="topBrandsDatasets"
                :labels="topBrandsLabels"
                :options="topBrandsOptions"
                style="height: 280px;"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Row 2: Stock state doughnut + Arrival status pie -->
      <section class="dash-section">
        <div class="charts-grid">
          <div class="chart-card">
            <div class="chart-header">
              <div>
                <h2 class="chart-title">Stock State Breakdown</h2>
                <p class="chart-subtitle">Live count by state · all stocks</p>
              </div>
            </div>
            <div class="chart-body chart-body-center">
              <CChartDoughnut
                :datasets="stockStateDatasets"
                :labels="stockStateLabels"
                :options="stockStateOptions"
                style="height: 280px;"
              />
            </div>
          </div>

          <div class="chart-card">
            <div class="chart-header">
              <div>
                <h2 class="chart-title">Arrival Status</h2>
                <p class="chart-subtitle">Open stocks · Incoming vs Received</p>
              </div>
            </div>
            <div class="chart-body chart-body-center">
              <CChartPie
                :datasets="arrivalDatasets"
                :labels="arrivalLabels"
                :options="arrivalOptions"
                style="height: 280px;"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Row 3: Inventory aging -->
      <section class="dash-section">
        <div class="chart-card">
          <div class="chart-header">
            <div>
              <h2 class="chart-title">Inventory Aging</h2>
              <p class="chart-subtitle">Open stocks by days since arrival</p>
            </div>
          </div>
          <div class="chart-body">
            <CChartBar
              :datasets="agingDatasets"
              :labels="agingLabels"
              :options="agingOptions"
              style="height: 260px;"
            />
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import {
  CChartLine,
  CChartBar,
  CChartDoughnut,
  CChartPie,
} from "@coreui/vue-chartjs";
import DashboardApi from "../lib/dashboardApi";
import WidgetsDashboardKpi from "./widgets/WidgetsDashboardKpi.vue";

const MONTH_NAMES_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const PALETTE = {
  primary: "#2563eb",
  primaryLight: "rgba(37, 99, 235, 0.6)",
  success: "#059669",
  successLight: "rgba(5, 150, 105, 0.6)",
  warning: "#d97706",
  warningLight: "rgba(217, 119, 6, 0.6)",
  danger: "#dc2626",
  info: "#7c3aed",
  neutral: "#6b7280",
  neutralLight: "rgba(107, 114, 128, 0.4)",
  // State palette — distinct, accessible
  open: "#10b981",
  booked: "#f59e0b",
  lou: "#3b82f6",
  sold: "#6366f1",
  cancelled: "#9ca3af",
  requestDuty: "#ef4444",
};

export default {
  name: "Dashboard",
  components: {
    CChartLine,
    CChartBar,
    CChartDoughnut,
    CChartPie,
    WidgetsDashboardKpi,
  },
  data() {
    return {
      api: new DashboardApi(),
      loading: true,
      errorMessage: "",
      infoList: [],
      dashboardData: {
        lastYearSales: { total: 0 },
        thisYearSales: { total: 0 },
        arrivalStatus: { incoming: 0, received: 0 },
        topSellingBrands: [],
        lastYearTopSellingBrands: [],
        topSellingModels: [],
        lastYearTopSellingModels: [],
        unitsRegisteredByMonth: [],
        unitsRegisteredByMonthLastYear: [],
        unitsRegisteredByYear: [],
        stockStateBreakdown: {
          open: 0,
          booked: 0,
          lou: 0,
          sold: 0,
          cancelled: 0,
          requestDuty: 0,
        },
        inventoryAging: { age0To30: 0, age30To60: 0, age60To90: 0, age90Plus: 0 },
      },
    };
  },
  computed: {
    currentMonth() {
      return new Date().getMonth() + 1;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    lastYearNumber() {
      return this.currentYear - 1;
    },
    currentMonthName() {
      return new Date().toLocaleString("en-MY", { month: "long" });
    },
    previousMonthName() {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      return d.toLocaleString("en-MY", { month: "short" });
    },
    asOfLabel() {
      const d = new Date();
      return `as of ${d.toLocaleDateString("en-MY", { day: "2-digit", month: "short", year: "numeric" })}`;
    },

    // === KPI numbers ===
    kpi() {
      const byMonth = this.dashboardData.unitsRegisteredByMonth || [];
      const byMonthLast = this.dashboardData.unitsRegisteredByMonthLastYear || [];
      const byYear = this.dashboardData.unitsRegisteredByYear || [];

      const thisMonth = byMonth.find((m) => m.month === this.currentMonth);
      const registeredThisMonth = thisMonth ? thisMonth.count : 0;

      const prevMonthNum = this.currentMonth === 1 ? 12 : this.currentMonth - 1;
      // Previous month is in this year unless we're in January, then it's last year
      const prevMonthSource = this.currentMonth === 1 ? byMonthLast : byMonth;
      const prevMonth = prevMonthSource.find((m) => m.month === prevMonthNum);
      const prevMonthValue = prevMonth ? prevMonth.count : 0;

      const ytdEntry = byYear.find((y) => y.year === this.currentYear);
      const registeredYtd = ytdEntry ? ytdEntry.count : 0;

      const lastYearTotalEntry = byYear.find((y) => y.year === this.lastYearNumber);
      const lastYearTotal = lastYearTotalEntry ? lastYearTotalEntry.count : 0;

      // Compare year-to-date: sum last year's months up to current month for a
      // fair comparison instead of full-year vs partial-year.
      const lastYearSameWindow = byMonthLast
        .filter((m) => m.month <= this.currentMonth)
        .reduce((sum, m) => sum + m.count, 0);

      const availableStock = (this.dashboardData.stockStateBreakdown || {}).open || 0;

      const soldYtd = (this.dashboardData.thisYearSales || {}).total || 0;
      const soldLast = (this.dashboardData.lastYearSales || {}).total || 0;

      return {
        registeredThisMonth,
        registeredThisMonthDelta: registeredThisMonth - prevMonthValue,
        registeredYtd,
        registeredYtdDelta: registeredYtd - lastYearSameWindow,
        availableStock,
        soldYtd,
        soldYtdDelta: soldYtd - soldLast,
      };
    },

    // === Monthly trend line chart ===
    trendLineLabels() {
      return MONTH_NAMES_SHORT;
    },
    trendLineDatasets() {
      const months = Array.from({ length: 12 }, (_, i) => i + 1);
      const thisYearData = months.map((m) => {
        const found = (this.dashboardData.unitsRegisteredByMonth || []).find((x) => x.month === m);
        return found ? found.count : 0;
      });
      const lastYearData = months.map((m) => {
        const found = (this.dashboardData.unitsRegisteredByMonthLastYear || []).find((x) => x.month === m);
        return found ? found.count : 0;
      });
      return [
        {
          label: `${this.currentYear}`,
          borderColor: PALETTE.primary,
          backgroundColor: "rgba(37, 99, 235, 0.08)",
          borderWidth: 2.5,
          fill: true,
          tension: 0.35,
          pointBackgroundColor: PALETTE.primary,
          pointRadius: 3,
          data: thisYearData,
        },
        {
          label: `${this.lastYearNumber}`,
          borderColor: PALETTE.neutral,
          backgroundColor: "transparent",
          borderWidth: 2,
          borderDash: [6, 4],
          fill: false,
          tension: 0.35,
          pointBackgroundColor: PALETTE.neutral,
          pointRadius: 2,
          data: lastYearData,
        },
      ];
    },
    trendLineOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
        },
        legend: { display: true, position: "bottom" },
      };
    },

    // === Top brands grouped bar ===
    // Union of both lists, ordered by this-year count desc. Single computed
    // source so labels + datasets stay in sync regardless of Vue's compute order.
    topBrandsMerged() {
      const map = new Map();
      (this.dashboardData.topSellingBrands || this.dashboardData.topSellingModels || []).forEach((b) => {
        map.set(b.name, { this: b.unitsSold, last: 0 });
      });
      (this.dashboardData.lastYearTopSellingBrands || this.dashboardData.lastYearTopSellingModels || []).forEach((b) => {
        const e = map.get(b.name) || { this: 0, last: 0 };
        e.last = b.unitsSold;
        map.set(b.name, e);
      });
      return Array.from(map.entries()).sort((a, b) => (b[1].this || 0) - (a[1].this || 0));
    },
    topBrandsLabels() {
      return this.topBrandsMerged.map(([name]) => name);
    },
    topBrandsDatasets() {
      const arr = this.topBrandsMerged;
      return [
        {
          label: `${this.currentYear}`,
          backgroundColor: PALETTE.success,
          data: arr.map(([, v]) => v.this || 0),
        },
        {
          label: `${this.lastYearNumber}`,
          backgroundColor: PALETTE.neutralLight,
          data: arr.map(([, v]) => v.last || 0),
        },
      ];
    },
    topBrandsOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
        },
        legend: { display: true, position: "bottom" },
      };
    },

    // === Stock state doughnut ===
    // Cancelled is intentionally omitted — business no longer tracks it on
    // the role-shared dashboard. Backend still computes it but it isn't shown.
    stockStateLabels() {
      return ["Available", "Booked", "LOU", "Sold", "Request Duty"];
    },
    stockStateDatasets() {
      const s = this.dashboardData.stockStateBreakdown || {};
      return [
        {
          backgroundColor: [
            PALETTE.open,
            PALETTE.booked,
            PALETTE.lou,
            PALETTE.sold,
            PALETTE.requestDuty,
          ],
          data: [
            s.open || 0,
            s.booked || 0,
            s.lou || 0,
            s.sold || 0,
            s.requestDuty || 0,
          ],
        },
      ];
    },
    stockStateOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: true, position: "right" },
        cutoutPercentage: 60,
      };
    },

    // === Arrival pie ===
    arrivalLabels() {
      return ["Incoming", "Received"];
    },
    arrivalDatasets() {
      const a = this.dashboardData.arrivalStatus || {};
      return [
        {
          backgroundColor: [PALETTE.warning, PALETTE.success],
          data: [a.incoming || 0, a.received || 0],
        },
      ];
    },
    arrivalOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: true, position: "right" },
      };
    },

    // === Inventory aging bar ===
    agingLabels() {
      return ["0–30 days", "30–60 days", "60–90 days", "90+ days"];
    },
    agingDatasets() {
      const a = this.dashboardData.inventoryAging || {};
      return [
        {
          label: "Open stocks",
          backgroundColor: [
            PALETTE.open,
            PALETTE.booked,
            PALETTE.warningLight,
            PALETTE.danger,
          ],
          data: [
            a.age0To30 || 0,
            a.age30To60 || 0,
            a.age60To90 || 0,
            a.age90Plus || 0,
          ],
        },
      ];
    },
    agingOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
        },
        legend: { display: false },
      };
    },
  },
  mounted() {
    this.loadDashboardData();
  },
  methods: {
    toast(header, message, color) {
      this.infoList.push({ date: Date.now(), header, message, color });
    },
    loadDashboardData() {
      this.loading = true;
      this.errorMessage = "";
      this.api
        .getTop()
        .then((response) => {
          this.dashboardData = response.result || this.dashboardData;
          // Backwards compat: if backend returned `topSellingModels` but not
          // `topSellingBrands` (older API), map them onto the new field too.
          if (!this.dashboardData.topSellingBrands && this.dashboardData.topSellingModels) {
            this.dashboardData.topSellingBrands = this.dashboardData.topSellingModels;
          }
          if (!this.dashboardData.lastYearTopSellingBrands && this.dashboardData.lastYearTopSellingModels) {
            this.dashboardData.lastYearTopSellingBrands = this.dashboardData.lastYearTopSellingModels;
          }
        })
        .catch((err) => {
          const data = err && err.data ? err.data : err;
          const message =
            (data && (data.message || (data.responseException && data.responseException.exceptionMessage))) ||
            "Server didn't respond.";
          this.errorMessage = message;
          this.toast("Dashboard", message, "danger");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.dashboard-wrapper {
  padding: 24px;
  min-height: calc(100vh - 56px);
  background: #f5f6f8;
}

.dash-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.dash-title {
  font-size: clamp(1.4rem, 2vw, 1.8rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #111827;
  margin: 0;
}
.dash-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.dash-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  gap: 16px;
  color: #6b7280;
}

.dash-error {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 20px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  margin-bottom: 16px;
}
.dash-error i { font-size: 1.4rem; color: #dc2626; margin-top: 2px; }
.dash-error strong { display: block; font-size: 1rem; }
.dash-error p { margin: 4px 0 8px; color: #7f1d1d; font-size: 0.9rem; }
.dash-retry-btn {
  background: #b91c1c;
  color: white;
  border: 0;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
}

.dash-section { margin-bottom: 20px; }

/* === KPI grid: 4 across on lg, 2x2 on md/sm, 1 col on xs === */
.kpi-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 576px) {
  .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 1200px) {
  .kpi-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

/* === Charts grid: 2 across on lg, 1 col below === */
.charts-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
@media (min-width: 992px) {
  .charts-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.chart-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #f1f3f5;
  overflow: hidden;
}
.chart-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f3f5;
}
.chart-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}
.chart-subtitle {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: #9ca3af;
}
.chart-body {
  padding: 16px 20px;
}
.chart-body-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 575.98px) {
  .dashboard-wrapper { padding: 16px; }
  .chart-header { padding: 12px 14px; }
  .chart-body { padding: 12px 14px; }
}

@media print {
  .dashboard-wrapper { background: white; padding: 0; }
  .chart-card { box-shadow: none; page-break-inside: avoid; }
}
</style>
